import * as THREE from 'three';
import type { FoodBlob, RemoteSnake, SelfState } from '../shared/protocol.js';
import { lookForId, type EyeStyle } from './wormLook.js';
import type { RenderOpts } from './render.js';

/**
 * Visual-only Three.js layer. The server simulation stays 2D; this module maps
 * world (x, y) onto the ground plane as (x, 0, y). The camera is a near-top-down
 * view that keeps the player's worm centred and zooms out as the worm grows.
 * Worms are drawn as tightly overlapping spheres with cartoon toon shading, a
 * subtle inverted-hull outline, a rubber-hose body wobble, and billboarded
 * pie-cut eyes. The default worm is only outlined while boosting.
 */

const BG = 0x050007;
const MAX_ORB = 6000;
const MAX_SEG = 4000;
const MAX_EYE = 256;
const MAX_PART = 3000;
const RING = 7;
const OUTLINE = 1.18;
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

type Particle = {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  life: number;
  max: number;
  hue: number;
  size: number;
};

type Label = {
  sprite: THREE.Sprite;
  tex: THREE.CanvasTexture;
  canvas: HTMLCanvasElement;
  text: string;
};

type BodySnapshot = { pts: number[]; hue: number };

export type View3D = {
  ok: boolean;
  renderer: THREE.WebGLRenderer | null;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  arena: THREE.Group;
  camX: number;
  camY: number;
  dist: number;
  shake: number;
  w: number;
  h: number;
  orbCore: THREE.InstancedMesh;
  orbGlow: THREE.InstancedMesh;
  orbOutline: THREE.InstancedMesh;
  segCore: THREE.InstancedMesh;
  segOutline: THREE.InstancedMesh;
  segGlow: THREE.InstancedMesh;
  eyeOpen: THREE.InstancedMesh;
  eyeClosed: THREE.InstancedMesh;
  eyePupil: THREE.InstancedMesh;
  partCore: THREE.InstancedMesh;
  partGlow: THREE.InstancedMesh;
  particles: Particle[];
  labels: Map<string, Label>;
  blink: Map<string, { next: number; until: number }>;
  bodies: Map<string, BodySnapshot>;
  trail: Array<{ x: number; z: number }>;
  spine: Float32Array;
  selfAlive: boolean;
  selfSegments: number;
  bodyLen: number;
  selfX: number;
  selfY: number;
  selfR: number;
  selfHue: number;
  last: number;
  quat: THREE.Quaternion;
  idq: THREE.Quaternion;
  pos: THREE.Vector3;
  scl: THREE.Vector3;
  mat: THREE.Matrix4;
  color: THREE.Color;
};

const _right = new THREE.Vector3();
const _up = new THREE.Vector3();
const _camZ = new THREE.Vector3();
const pen = { si: 0, gi: 0, eo: 0, ec: 0, ep: 0 };

function glowTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 64;
  c.height = 64;
  const g = c.getContext('2d');
  if (g) {
    const rad = g.createRadialGradient(32, 32, 0, 32, 32, 32);
    rad.addColorStop(0, 'rgba(255,255,255,1)');
    rad.addColorStop(0.22, 'rgba(255,255,255,0.7)');
    rad.addColorStop(0.55, 'rgba(255,255,255,0.16)');
    rad.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = rad;
    g.fillRect(0, 0, 64, 64);
  }
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

function toonGradient(): THREE.DataTexture {
  const data = new Uint8Array([110, 170, 225, 255]);
  const tex = new THREE.DataTexture(data, data.length, 1, THREE.RedFormat);
  tex.minFilter = THREE.NearestFilter;
  tex.magFilter = THREE.NearestFilter;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  return tex;
}

const INK = '#170010';

function canvasTex(c: HTMLCanvasElement): THREE.CanvasTexture {
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}

function lash(g: CanvasRenderingContext2D, x: number, y: number, dx: number, dy: number): void {
  g.beginPath();
  g.moveTo(x, y);
  g.lineTo(x + dx, y + dy);
  g.stroke();
}

/** Open eye: white sclera with an upright upper eyelid and lashes. */
function openEyeTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 128;
  c.height = 128;
  const g = c.getContext('2d');
  if (g) {
    const cx = 64;
    const cy = 66;
    const rx = 50;
    const ry = 46;
    g.save();
    g.beginPath();
    g.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
    g.fillStyle = '#ffffff';
    g.fill();
    g.clip();
    g.fillStyle = INK;
    g.beginPath();
    g.moveTo(cx - rx - 2, cy - ry - 2);
    g.lineTo(cx + rx + 2, cy - ry - 2);
    g.lineTo(cx + rx + 2, cy - ry * 0.34);
    g.quadraticCurveTo(cx, cy - ry * 0.74, cx - rx - 2, cy - ry * 0.34);
    g.closePath();
    g.fill();
    g.strokeStyle = 'rgba(255,255,255,0.22)';
    g.lineWidth = 3;
    g.beginPath();
    g.moveTo(cx - rx, cy - ry * 0.34);
    g.quadraticCurveTo(cx, cy - ry * 0.74, cx + rx, cy - ry * 0.34);
    g.stroke();
    g.strokeStyle = INK;
    g.lineWidth = 5;
    g.beginPath();
    g.moveTo(cx - rx * 0.8, cy + ry * 0.7);
    g.quadraticCurveTo(cx, cy + ry * 0.96, cx + rx * 0.8, cy + ry * 0.7);
    g.stroke();
    g.restore();
    g.lineWidth = 6;
    g.lineCap = 'round';
    g.strokeStyle = INK;
    lash(g, cx - rx * 0.72, cy - ry * 0.62, -14, -16);
    lash(g, cx - rx * 0.32, cy - ry * 0.76, -9, -20);
    lash(g, cx + rx * 0.72, cy - ry * 0.62, 14, -16);
    lash(g, cx + rx * 0.32, cy - ry * 0.76, 9, -20);
    g.lineWidth = 5;
    g.beginPath();
    g.ellipse(cx, cy, rx - 4, ry - 4, 0, 0, Math.PI * 2);
    g.strokeStyle = INK;
    g.stroke();
  }
  return canvasTex(c);
}

/** Closed eye: a bold downward lid arc with lashes. */
function closedEyeTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 128;
  c.height = 128;
  const g = c.getContext('2d');
  if (g) {
    const cx = 64;
    const cy = 64;
    const rx = 50;
    g.lineCap = 'round';
    g.strokeStyle = INK;
    g.lineWidth = 9;
    g.beginPath();
    g.moveTo(cx - rx * 0.82, cy - 4);
    g.quadraticCurveTo(cx, cy + 34, cx + rx * 0.82, cy - 4);
    g.stroke();
    g.lineWidth = 6;
    lash(g, cx - rx * 0.62, cy + 8, -12, -14);
    lash(g, cx - rx * 0.2, cy + 22, -6, -18);
    lash(g, cx + rx * 0.62, cy + 8, 12, -14);
    lash(g, cx + rx * 0.2, cy + 22, 6, -18);
  }
  return canvasTex(c);
}

/** Pupil layer with two cartoon highlights. */
function pupilTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 128;
  c.height = 128;
  const g = c.getContext('2d');
  if (g) {
    const cx = 64;
    const cy = 64;
    g.fillStyle = INK;
    g.beginPath();
    g.ellipse(cx, cy, 40, 44, 0, 0, Math.PI * 2);
    g.fill();
    g.fillStyle = '#ffffff';
    g.beginPath();
    g.arc(cx - 14, cy - 16, 13, 0, Math.PI * 2);
    g.fill();
    g.beginPath();
    g.arc(cx + 16, cy + 18, 6, 0, Math.PI * 2);
    g.fill();
  }
  return canvasTex(c);
}

function instanced(geo: THREE.BufferGeometry, mat: THREE.Material, count: number): THREE.InstancedMesh {
  const mesh = new THREE.InstancedMesh(geo, mat, count);
  mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  mesh.frustumCulled = false;
  mesh.setColorAt(0, new THREE.Color(1, 1, 1));
  mesh.count = 0;
  return mesh;
}

function instancedPlain(geo: THREE.BufferGeometry, mat: THREE.Material, count: number): THREE.InstancedMesh {
  const mesh = new THREE.InstancedMesh(geo, mat, count);
  mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  mesh.frustumCulled = false;
  mesh.count = 0;
  return mesh;
}

function glowMaterial(tex: THREE.Texture): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial({
    map: tex,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide,
    toneMapped: false,
    fog: false,
  });
}

export function makeView3d(canvas: HTMLCanvasElement): View3D {
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(BG);
  scene.fog = new THREE.Fog(BG, 3200, 9000);

  const camera = new THREE.PerspectiveCamera(55, 1, 10, 60000);

  scene.add(new THREE.AmbientLight(0xffffff, 0.62));
  const key = new THREE.DirectionalLight(0xffffff, 1.55);
  key.position.set(0.4, 1.5, 0.5);
  scene.add(key);
  const fill = new THREE.DirectionalLight(0xff5aa8, 0.5);
  fill.position.set(-0.6, 0.6, -0.5);
  scene.add(fill);

  const arena = new THREE.Group();
  const ground = new THREE.Mesh(
    new THREE.CircleGeometry(1, 96),
    new THREE.MeshStandardMaterial({ color: 0x120109, roughness: 1, metalness: 0 }),
  );
  ground.rotation.x = -Math.PI / 2;
  const ring = new THREE.Mesh(
    new THREE.TorusGeometry(1, 0.014, 8, 200),
    new THREE.MeshBasicMaterial({
      color: 0xff2d95,
      transparent: true,
      opacity: 0.9,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
      fog: false,
    }),
  );
  ring.rotation.x = -Math.PI / 2;
  ring.position.y = 0.004;
  const grid = new THREE.GridHelper(2, 48, 0xff2d95, 0x4a0d28);
  const gm = grid.material as THREE.Material;
  gm.transparent = true;
  gm.opacity = 0.16;
  gm.depthWrite = false;
  grid.position.y = 0.002;
  arena.add(ground, ring, grid);
  scene.add(arena);

  const tex = glowTexture();
  const gradient = toonGradient();
  const coreGeo = new THREE.SphereGeometry(1, 10, 8);
  const quadGeo = new THREE.PlaneGeometry(2, 2);
  const orbCore = instanced(coreGeo, new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.3, metalness: 0.1 }), MAX_ORB);
  const orbOutline = instancedPlain(coreGeo, new THREE.MeshBasicMaterial({ color: 0x0a0006, side: THREE.BackSide, toneMapped: false, fog: false }), MAX_ORB);
  const orbGlow = instanced(quadGeo, glowMaterial(tex), MAX_ORB);
  const segCore = instanced(coreGeo, new THREE.MeshToonMaterial({ color: 0xffffff, gradientMap: gradient }), MAX_SEG);
  const segOutline = instancedPlain(
    coreGeo,
    new THREE.MeshBasicMaterial({ color: 0x0a0006, side: THREE.BackSide, toneMapped: false, fog: false }),
    MAX_SEG,
  );
  const segGlow = instanced(quadGeo, glowMaterial(tex), MAX_SEG);
  const eyeMat = (map: THREE.Texture): THREE.MeshBasicMaterial =>
    new THREE.MeshBasicMaterial({ map, transparent: true, depthWrite: false, side: THREE.DoubleSide, toneMapped: false, fog: false });
  const eyeOpen = instancedPlain(quadGeo, eyeMat(openEyeTexture()), MAX_EYE);
  const eyeClosed = instancedPlain(quadGeo, eyeMat(closedEyeTexture()), MAX_EYE);
  const eyePupil = instancedPlain(quadGeo, eyeMat(pupilTexture()), MAX_EYE);
  eyeOpen.renderOrder = 1;
  eyeClosed.renderOrder = 1;
  eyePupil.renderOrder = 2;
  const partCore = instanced(coreGeo, new THREE.MeshBasicMaterial({ color: 0xffffff, toneMapped: false }), MAX_PART);
  const partGlow = instanced(quadGeo, glowMaterial(tex), MAX_PART);
  scene.add(orbCore, orbGlow, orbOutline, segCore, segOutline, segGlow, eyeOpen, eyeClosed, eyePupil, partCore, partGlow);

  const view: View3D = {
    ok: false,
    renderer: null,
    scene,
    camera,
    arena,
    camX: 0,
    camY: 0,
    dist: 1,
    shake: 0,
    w: 1,
    h: 1,
    orbCore,
    orbGlow,
    orbOutline,
    segCore,
    segOutline,
    segGlow,
    eyeOpen,
    eyeClosed,
    eyePupil,
    partCore,
    partGlow,
    particles: [],
    labels: new Map(),
    blink: new Map(),
    bodies: new Map(),
    trail: [],
    spine: new Float32Array(220 * 3),
    selfAlive: false,
    selfSegments: 0,
    bodyLen: 0,
    selfX: 0,
    selfY: 0,
    selfR: 12,
    selfHue: 338,
    last: 0,
    quat: new THREE.Quaternion(),
    idq: new THREE.Quaternion(),
    pos: new THREE.Vector3(),
    scl: new THREE.Vector3(),
    mat: new THREE.Matrix4(),
    color: new THREE.Color(),
  };

  try {
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false, powerPreference: 'high-performance' });
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setClearColor(BG, 1);
    view.renderer = renderer;
    view.ok = true;
  } catch {
    view.ok = false;
    view.renderer = null;
  }
  return view;
}

function put(view: View3D, mesh: THREE.InstancedMesh, i: number, x: number, y: number, z: number, s: number, flat: boolean): void {
  view.pos.set(x, y, z);
  if (flat) view.scl.set(s, s, 1);
  else view.scl.set(s, s, s);
  view.mat.compose(view.pos, flat ? view.quat : view.idq, view.scl);
  mesh.setMatrixAt(i, view.mat);
}

function putQ(view: View3D, mesh: THREE.InstancedMesh, i: number, x: number, y: number, z: number, s: number, q: THREE.Quaternion): void {
  view.pos.set(x, y, z);
  view.scl.set(s, s, s);
  view.mat.compose(view.pos, q, view.scl);
  mesh.setMatrixAt(i, view.mat);
}

function flush(mesh: THREE.InstancedMesh, n: number): void {
  mesh.count = n;
  mesh.instanceMatrix.needsUpdate = true;
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
}

function emitSphere(view: View3D, x: number, y: number, z: number, scale: number, color: THREE.Color, glow: THREE.Color | null): void {
  if (pen.si >= MAX_SEG) return;
  const i = pen.si++;
  put(view, view.segCore, i, x, y, z, scale, false);
  view.segCore.setColorAt(i, color);
  put(view, view.segOutline, i, x, y, z, scale * OUTLINE, false);
  if (glow && pen.gi < MAX_SEG) {
    const g = pen.gi++;
    put(view, view.segGlow, g, x, y, z, scale * 2.4, true);
    view.segGlow.setColorAt(g, glow);
  }
}

function addParticle(view: View3D, x: number, y: number, z: number, hue: number, size: number, vx?: number, vy?: number, vz?: number): void {
  if (view.particles.length >= MAX_PART) return;
  const a = Math.random() * Math.PI * 2;
  const sp = reduceMotion ? 0 : 30 + Math.random() * 50;
  view.particles.push({
    x,
    y,
    z,
    vx: vx ?? Math.cos(a) * sp,
    vy: vy ?? 45 + Math.random() * 60,
    vz: vz ?? Math.sin(a) * sp,
    life: 1.2 + Math.random() * 0.7,
    max: 1.9,
    hue: hue + (Math.random() * 20 - 10),
    size,
  });
}

function spawnBurst(view: View3D, pts: number[], hue: number, fallbackR: number): void {
  const n = Math.floor(pts.length / 3);
  if (n === 0) {
    addParticle(view, view.selfX, fallbackR, view.selfY, hue, fallbackR);
    return;
  }
  for (let i = 0; i < n; i++) {
    const x = pts[i * 3] ?? 0;
    const z = pts[i * 3 + 1] ?? 0;
    const r = (pts[i * 3 + 2] ?? fallbackR * 10) / 10;
    addParticle(view, x, r * 0.9, z, hue, r * 0.9);
  }
}

const scratch = new THREE.Color();
function hexToHSL(value: string): { h: number; s: number; l: number } {
  scratch.set(value);
  const hsl = { h: 0, s: 0, l: 0 };
  scratch.getHSL(hsl);
  return hsl;
}

function hexToHue(value: string): number {
  return hexToHSL(value).h * 360;
}

function eyeShape(style: EyeStyle): { r: number; lift: number; fwd: number; side: number } {
  if (style === 'wide') return { r: 0.44, lift: 1.02, fwd: 0.3, side: 0.52 };
  if (style === 'sleepy') return { r: 0.34, lift: 0.94, fwd: 0.34, side: 0.42 };
  if (style === 'sharp') return { r: 0.3, lift: 1.0, fwd: 0.38, side: 0.46 };
  return { r: 0.4, lift: 1.0, fwd: 0.3, side: 0.5 };
}

function drawEyes(view: View3D, key: string, hx: number, hz: number, r: number, angle: number, style: EyeStyle, timestamp: number): void {
  const shape = eyeShape(style);
  const headScale = r * 1.35;
  const eyeR = headScale * shape.r;
  const y = headScale * (0.92 + shape.lift);
  const fx = Math.cos(angle);
  const fz = Math.sin(angle);
  const sideX = -fz;
  const sideZ = fx;
  const fwd = headScale * shape.fwd;
  const side = headScale * shape.side;

  let b = view.blink.get(key);
  if (!b) {
    b = { next: timestamp + 1500 + Math.random() * 4500, until: 0 };
    view.blink.set(key, b);
  }
  if (timestamp >= b.next && timestamp >= b.until) {
    b.until = timestamp + 130;
    b.next = timestamp + 1800 + Math.random() * 5000;
  }
  const closed = timestamp < b.until;

  _right.set(1, 0, 0).applyQuaternion(view.quat);
  _up.set(0, 1, 0).applyQuaternion(view.quat);
  _camZ.set(0, 0, 1).applyQuaternion(view.quat);
  const sdx = fx * _right.x + fz * _right.z;
  const sdy = fx * _up.x + fz * _up.z;
  const dl = Math.hypot(sdx, sdy) || 1;
  const ox = (sdx / dl) * eyeR * 0.16;
  const oy = (sdy / dl) * eyeR * 0.16;

  for (const sgn of [-1, 1]) {
    const ex = hx + fx * fwd + sideX * side * sgn;
    const ez = hz + fz * fwd + sideZ * side * sgn;
    if (closed) {
      if (pen.ec < MAX_EYE) {
        putQ(view, view.eyeClosed, pen.ec, ex, y, ez, eyeR, view.quat);
        pen.ec++;
      }
      continue;
    }
    if (pen.eo < MAX_EYE) {
      putQ(view, view.eyeOpen, pen.eo, ex, y, ez, eyeR, view.quat);
      pen.eo++;
    }
    if (pen.ep < MAX_EYE) {
      const px = ex + _right.x * ox + _up.x * oy + _camZ.x * eyeR * 0.04;
      const py = y + _right.y * ox + _up.y * oy + _camZ.y * eyeR * 0.04;
      const pz = ez + _right.z * ox + _up.z * oy + _camZ.z * eyeR * 0.04;
      putQ(view, view.eyePupil, pen.ep, px, py, pz, eyeR, view.quat);
      pen.ep++;
    }
  }
}

/** Resample the self trail into rings spaced one body-width apart, extending
 *  straight back when the trail is short (e.g. at spawn). `countF` may be
 *  fractional so the tail can slide out smoothly from the back when growing.
 *  Ring x/z live in slots 0/1 and the ring's grow factor (0..1) lives in slot 2. */
function buildSpine(view: View3D, self: SelfState, countF: number): number {
  const out = view.spine;
  const cap = Math.floor(out.length / 3);
  const maxN = Math.max(1, Math.min(Math.floor(countF), cap));
  out[0] = self.x;
  out[1] = self.y;
  out[2] = 1;
  let placed = 1;
  let segX = self.x;
  let segZ = self.y;
  let acc = 0;
  const trail = view.trail;
  let idx = trail.length - 1;
  const bx = -Math.cos(self.angle);
  const bz = -Math.sin(self.angle);
  let guard = 0;
  while (placed < maxN && guard++ < 100000) {
    let nx: number;
    let nz: number;
    if (idx >= 0) {
      const p = trail[idx]!;
      nx = p.x;
      nz = p.z;
    } else {
      nx = segX + bx * RING;
      nz = segZ + bz * RING;
    }
    const d = Math.hypot(nx - segX, nz - segZ);
    if (d < 1e-6) {
      if (idx >= 0) idx--;
      continue;
    }
    if (acc + d >= RING) {
      const t = (RING - acc) / d;
      const cx = segX + (nx - segX) * t;
      const cz = segZ + (nz - segZ) * t;
      out[placed * 3] = cx;
      out[placed * 3 + 1] = cz;
      out[placed * 3 + 2] = 1;
      placed++;
      acc = 0;
      if (t >= 0.999) {
        segX = nx;
        segZ = nz;
        if (idx >= 0) idx--;
      } else {
        segX = cx;
        segZ = cz;
      }
    } else {
      acc += d;
      segX = nx;
      segZ = nz;
      if (idx >= 0) idx--;
    }
  }
  const frac = Math.min(0.98, Math.max(0, countF - placed));
  if (frac > 0.02 && placed < cap) {
    let dx: number;
    let dz: number;
    if (placed >= 2) {
      dx = (out[(placed - 1) * 3] ?? 0) - (out[(placed - 2) * 3] ?? 0);
      dz = (out[(placed - 1) * 3 + 1] ?? 0) - (out[(placed - 2) * 3 + 1] ?? 0);
    } else {
      dx = bx * RING;
      dz = bz * RING;
    }
    const len = Math.hypot(dx, dz) || 1;
    out[placed * 3] = (out[(placed - 1) * 3] ?? 0) + (dx / len) * frac * RING;
    out[placed * 3 + 1] = (out[(placed - 1) * 3 + 1] ?? 0) + (dz / len) * frac * RING;
    out[placed * 3 + 2] = frac;
    placed++;
  }
  return placed;
}

function createLabel(scene: THREE.Scene, text: string): Label {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 64;
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
    map: tex,
    transparent: true,
    depthTest: false,
    depthWrite: false,
    toneMapped: false,
  }));
  sprite.scale.set(360, 90, 1);
  scene.add(sprite);
  const label: Label = { sprite, tex, canvas, text: '' };
  drawLabel(label, text);
  return label;
}

function drawLabel(label: Label, text: string): void {
  const g = label.canvas.getContext('2d');
  if (!g) return;
  g.clearRect(0, 0, label.canvas.width, label.canvas.height);
  g.font = '700 34px Inter, system-ui, sans-serif';
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.shadowColor = 'rgba(255,45,149,0.95)';
  g.shadowBlur = 14;
  g.fillStyle = 'rgba(255,255,255,0.96)';
  g.fillText(text.slice(0, 16), label.canvas.width / 2, label.canvas.height / 2);
  label.text = text;
  label.tex.needsUpdate = true;
}

export function render3d(
  view: View3D,
  self: SelfState | null,
  snakes: RemoteSnake[],
  food: FoodBlob[],
  worldRadius: number,
  timestamp: number,
  opts: RenderOpts = {},
): void {
  if (!view.ok || !view.renderer) return;
  const camera = view.camera;

  const dt = view.last > 0 ? Math.min(0.05, Math.max(0.001, (timestamp - view.last) / 1000)) : 0.016;
  view.last = timestamp;

  const canvas = view.renderer.domElement;
  const w = canvas.clientWidth || window.innerWidth;
  const h = canvas.clientHeight || window.innerHeight;
  if (w !== view.w || h !== view.h) {
    view.w = w;
    view.h = h;
    view.renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  }

  const alive = !!self?.alive;
  const selfLook = opts.look;
  if (selfLook) view.selfHue = hexToHue(selfLook.a);

  if (alive && self) {
    const lastPoint = view.trail[view.trail.length - 1];
    if (!lastPoint || Math.hypot(self.x - lastPoint.x, self.y - lastPoint.z) > 2) {
      view.trail.push({ x: self.x, z: self.y });
      const cap = Math.min(3600, self.segments * 6 + 60);
      if (view.trail.length > cap) view.trail.splice(0, view.trail.length - cap);
    }
    if (view.selfAlive && self.segments > view.selfSegments && !reduceMotion) {
      const gained = Math.min(6, self.segments - view.selfSegments);
      for (let i = 0; i < gained * 4; i++) {
        addParticle(view, self.x, self.radius * 1.1, self.y, view.selfHue, self.radius * 0.5);
      }
      view.shake = Math.min(26, view.shake + gained * 3);
    }
    view.selfX = self.x;
    view.selfY = self.y;
    view.selfR = self.radius;
    view.selfSegments = self.segments;
    if (!view.selfAlive) view.bodyLen = self.segments;
    else view.bodyLen += (self.segments - view.bodyLen) * Math.min(1, dt * 9);
  }

  if (view.selfAlive && !alive) {
    const trail = view.trail;
    const count = Math.min(trail.length, 24);
    for (let i = 0; i < count; i++) {
      const p = trail[trail.length - 1 - i]!;
      addParticle(view, p.x, view.selfR * 0.9, p.z, view.selfHue, view.selfR * 0.9);
    }
    if (count === 0) addParticle(view, view.selfX, view.selfR, view.selfY, view.selfHue, view.selfR);
    view.shake = Math.min(40, view.shake + 24);
  }
  view.selfAlive = alive;
  if (!alive) {
    view.trail.length = 0;
    view.selfSegments = 0;
    view.bodyLen = 0;
  }

  if (self?.alive) {
    const target = Math.max(0.5, Math.min(2.6, 0.5 + (self.segments - 18) * 0.011 + (self.radius - 12) * 0.008));
    view.dist += (target - view.dist) * 0.05;
    view.camX = self.x;
    view.camY = self.y;
  } else if (self) {
    view.dist += (0.9 - view.dist) * 0.06;
    view.camX += (self.x - view.camX) * 0.15;
    view.camY += (self.y - view.camY) * 0.15;
  } else {
    view.dist += (1 - view.dist) * 0.04;
    view.camX = Math.cos(timestamp / 9000) * 140;
    view.camY = Math.sin(timestamp / 11000) * 80;
  }

  const camH = 1500 * view.dist;
  const camBack = camH * 0.14;
  const sh = reduceMotion ? 0 : view.shake;
  camera.position.set(
    view.camX + (Math.random() - 0.5) * sh,
    camH + (Math.random() - 0.5) * sh,
    view.camY + camBack + (Math.random() - 0.5) * sh,
  );
  camera.lookAt(view.camX, 0, view.camY);
  camera.updateMatrixWorld();
  view.quat.copy(camera.quaternion);
  view.shake *= 0.85;

  view.arena.scale.setScalar(worldRadius);

  const pulse = reduceMotion ? 1 : 0.85 + Math.sin(timestamp * 0.004) * 0.15;

  let oi = 0;
  for (let i = 0; i < food.length && oi < MAX_ORB; i++) {
    const blob = food[i]!;
    const hue = blob[2];
    const size = blob[3] > 0 ? blob[3] : 2;
    const coreR = (3.2 + size * 1.8) * 1.6;
    const glowR = (10 + Math.max(0.4, size) * 8) * 1.6 * pulse;
    const bob = reduceMotion ? 0 : Math.sin(timestamp * 0.003 + blob[0] * 0.05) * coreR * 0.25;
    const x = blob[0];
    const z = blob[1];
    const y = coreR + bob;
    put(view, view.orbCore, oi, x, y, z, coreR, false);
    view.orbCore.setColorAt(oi, view.color.setHSL(hue / 360, 0.9, 0.6));
    put(view, view.orbOutline, oi, x, y, z, coreR * 1.12, false);
    put(view, view.orbGlow, oi, x, y, z, glowR, true);
    view.orbGlow.setColorAt(oi, view.color.setHSL(hue / 360, 1, 0.62));
    oi++;
  }
  flush(view.orbCore, oi);
  flush(view.orbOutline, oi);
  flush(view.orbGlow, oi);

  pen.si = 0;
  pen.gi = 0;
  pen.eo = 0;
  pen.ec = 0;
  pen.ep = 0;
  const now = new Set<string>();
  const selfBody = selfLook ? hexToHSL(selfLook.a) : { h: 338 / 360, s: 0.85, l: 0.6 };
  const selfGlow = selfLook ? selfLook.glow : '#ff2d95';

  for (const snake of snakes) {
    now.add(snake.id);
    const raw = snake.body;
    const n = Math.floor(raw.length / 3);
    if (n === 0) continue;
    const hue = snake.color.h;
    const look = lookForId(snake.id);
    const headX = raw[0] ?? 0;
    const headZ = raw[1] ?? 0;
    const headR = (raw[2] ?? 120) / 10;
    let angle = 0;
    if (n > 1) {
      const nx = raw[3] ?? headX;
      const nz = raw[4] ?? headZ;
      angle = Math.atan2(headZ - nz, headX - nx);
    }
    const glow = snake.boosting ? view.color.set(look.glow).clone() : null;
    for (let i = 0; i < n; i++) {
      const r = (raw[i * 3 + 2] ?? 120) / 10;
      let x = raw[i * 3] ?? 0;
      let z = raw[i * 3 + 1] ?? 0;
      const t = i / Math.max(1, n - 1);
      const scale = r;
      if (!reduceMotion && i > 0 && n > 2) {
        const px = raw[(i - 1) * 3] ?? x;
        const pz = raw[(i - 1) * 3 + 1] ?? z;
        const dx = x - px;
        const dz = z - pz;
        const len = Math.hypot(dx, dz);
        if (len > 1e-3) {
          const off = Math.sin(timestamp * 0.006 - i * 0.55) * r * 0.07;
          x += (-dz / len) * off;
          z += (dx / len) * off;
        }
      }
      emitSphere(view, x, scale * 0.92, z, scale, view.color.setHSL(hue / 360, 0.85, i === 0 ? 0.64 : 0.6 - t * 0.12), glow);
    }
    drawEyes(view, snake.id, headX, headZ, headR, angle, look.eyes, timestamp);
    if (snake.boosting && !reduceMotion) {
      const fx = Math.cos(angle);
      const fz = Math.sin(angle);
      addParticle(view, headX - fx * headR * 2.4, headR * 0.8, headZ - fz * headR * 2.4, hue, headR * 0.5, -fx * 120, 20, -fz * 120);
    }
  }

  if (self?.alive) {
    const count = buildSpine(view, self, view.bodyLen);
    const spine = view.spine;
    const glow = self.boosting ? view.color.set(selfGlow).clone() : null;
    for (let i = 0; i < count; i++) {
      let x = spine[i * 3] ?? 0;
      let z = spine[i * 3 + 1] ?? 0;
      const grow = spine[i * 3 + 2] ?? 1;
      const t = i / Math.max(1, count - 1);
      const scale = self.radius * grow;
      if (!reduceMotion && i > 0 && count > 2) {
        const px = spine[(i - 1) * 3] ?? x;
        const pz = spine[(i - 1) * 3 + 1] ?? z;
        const nx = spine[(i + 1) * 3] ?? x;
        const nz = spine[(i + 1) * 3 + 1] ?? z;
        const dx = nx - px;
        const dz = nz - pz;
        const len = Math.hypot(dx, dz);
        if (len > 1e-3) {
          const off = Math.sin(timestamp * 0.006 - i * 0.55) * self.radius * 0.07;
          x += (-dz / len) * off;
          z += (dx / len) * off;
        }
      }
      emitSphere(view, x, scale * 0.92, z, scale, view.color.setHSL(selfBody.h, selfBody.s, i === 0 ? selfBody.l + 0.04 : selfBody.l - t * 0.12), glow);
    }
    drawEyes(view, 'self', self.x, self.y, self.radius, self.angle, selfLook?.eyes ?? 'round', timestamp);
    if (self.boosting && !reduceMotion && Math.random() < 0.85) {
      const fx = Math.cos(self.angle);
      const fz = Math.sin(self.angle);
      addParticle(view, self.x - fx * self.radius * 2.4, self.radius * 0.8, self.y - fz * self.radius * 2.4, view.selfHue, self.radius * 0.55, -fx * 150, 25, -fz * 150);
    }
  }
  flush(view.segCore, pen.si);
  flush(view.segOutline, pen.si);
  flush(view.segGlow, pen.gi);
  flush(view.eyeOpen, pen.eo);
  flush(view.eyeClosed, pen.ec);
  flush(view.eyePupil, pen.ep);

  if (alive) {
    for (const [id, snap] of view.bodies) {
      if (!now.has(id)) {
        spawnBurst(view, snap.pts, snap.hue, 12);
        view.bodies.delete(id);
        view.shake = Math.min(40, view.shake + 30);
      }
    }
    for (const snake of snakes) {
      if (snake.body.length < 3) {
        view.bodies.delete(snake.id);
        continue;
      }
      view.bodies.set(snake.id, { pts: snake.body, hue: snake.color.h });
    }
    for (const key of view.blink.keys()) {
      if (key !== 'self' && !now.has(key)) view.blink.delete(key);
    }
  } else {
    view.bodies.clear();
    view.blink.clear();
  }

  for (const [id, label] of view.labels) {
    label.sprite.visible = false;
    if (!alive || !now.has(id)) continue;
    const snake = snakes.find((s) => s.id === id);
    if (!snake) continue;
    const headX = snake.body[0] ?? 0;
    const headZ = snake.body[1] ?? 0;
    const headR = (snake.body[2] ?? 120) / 10;
    if (label.text !== snake.name) drawLabel(label, snake.name);
    label.sprite.position.set(headX, headR * 3.2, headZ);
    label.sprite.visible = true;
  }
  if (alive) {
    for (const snake of snakes) {
      if (view.labels.has(snake.id)) continue;
      view.labels.set(snake.id, createLabel(view.scene, snake.name));
    }
  }

  const parts = view.particles;
  for (let i = parts.length - 1; i >= 0; i--) {
    const p = parts[i]!;
    p.life -= dt;
    if (p.life <= 0) {
      parts.splice(i, 1);
      continue;
    }
    p.x += p.vx * dt;
    p.y += p.vy * dt;
    p.z += p.vz * dt;
    p.vx *= 1 - 2.2 * dt;
    p.vz *= 1 - 2.2 * dt;
    p.vy *= 1 - 1.6 * dt;
    if (p.y < p.size * 0.5) p.y = p.size * 0.5;
  }
  let pi = 0;
  for (let i = 0; i < parts.length && pi < MAX_PART; i++) {
    const p = parts[i]!;
    const fade = Math.max(0, p.life / p.max);
    const ease = fade * fade;
    const s = p.size * (0.7 + 0.6 * (1 - fade));
    const hh = (((p.hue % 360) + 360) % 360) / 360;
    put(view, view.partCore, pi, p.x, p.y, p.z, s, false);
    view.partCore.setColorAt(pi, view.color.setHSL(hh, 0.9, 0.6 * ease + 0.05));
    put(view, view.partGlow, pi, p.x, p.y, p.z, s * 2.6, true);
    view.partGlow.setColorAt(pi, view.color.setHSL(hh, 1, 0.6 * ease));
    pi++;
  }
  flush(view.partCore, pi);
  flush(view.partGlow, pi);

  view.renderer.render(view.scene, camera);
}
